// WORKFORCE: rename a DE — for a person who may intervene on it (`can_intervene`: holding,
// supervising or above its seat) but is not a manager, so the full Edit form is not theirs. The
// API accepts only `display_name` (and `status`) from such a person.
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  toast,
  workerName,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { DeProfile } from "../../api/types";
import type { CompanyAccess } from "../company";
import { keys } from "../queries";
import { useDeNames } from "./de-name";
import { problemText } from "./de-status";

export function RenameDeDialog({
  api,
  company,
  de,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  de: Pick<DeProfile, "membership_id" | "display_name"> & { person_name?: string | null };
  onClose: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const [name, setName] = useState(de.display_name);
  // HELPERS: the optional person name, offered when the company names DEs by person.
  const deNames = useDeNames(api, company.id);
  const [person, setPerson] = useState(de.person_name ?? "");
  const withPerson = deNames === "person" || !!de.person_name;
  const save = useMutation({
    mutationFn: (body: { display_name?: string; person_name?: string | null }) =>
      api.client.digitalEmployees.update({
        params: { co: company.id, id: de.membership_id },
        body,
      }),
    onSuccess: async (_d, { display_name = de.display_name }) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: keys.de(company.id, de.membership_id) }),
        qc.invalidateQueries({ queryKey: keys.digitalEmployees(company.id) }),
      ]);
      toast.add({ title: `Renamed to ${workerName(display_name)}`, type: "success" });
      onClose();
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The name was not changed."), type: "error" }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = name.trim();
    if (!next) return;
    const nextPerson = person.trim() || null;
    const body = {
      ...(next !== de.display_name ? { display_name: next } : {}),
      ...(withPerson && nextPerson !== (de.person_name ?? null) ? { person_name: nextPerson } : {}),
    };
    if (Object.keys(body).length === 0) onClose();
    else save.mutate(body);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[440px]">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Rename {workerName(de.display_name)}</DialogTitle>
            <DialogDescription>The name people see in Chat, Work and the chart.</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={`${id}-name`}>Name</FieldLabel>
            <Input
              id={`${id}-name`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
            />
          </Field>
          {withPerson ? (
            <Field>
              <FieldLabel htmlFor={`${id}-person`}>Person name</FieldLabel>
              <Input
                id={`${id}-person`}
                maxLength={80}
                placeholder="Maya"
                value={person}
                onChange={(e) => setPerson(e.target.value)}
              />
            </Field>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
